import React, { useEffect, useMemo, useState } from 'react'
import { motion } from 'framer-motion'
import { MessageSquare, Clock, CheckCircle, AlertCircle } from 'lucide-react'
import { useAdminData } from '../contexts/AdminDataContext'

const Tickets = () => {
  const { chamados, loadChamados, responderChamado, atualizarStatusChamado, loading } = useAdminData()

  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [respostaTexto, setRespostaTexto] = useState('')
  const [statusEdicao, setStatusEdicao] = useState<'aberto' | 'em_andamento' | 'resolvido' | 'fechado'>('aberto')
  const [saving, setSaving] = useState(false)

  const selected = useMemo(() => chamados.find(c => c.id === selectedId), [chamados, selectedId])

  useEffect(() => {
    loadChamados()
  }, [])

  useEffect(() => {
    if (selected) {
      setRespostaTexto(selected.resposta || '')
      setStatusEdicao(selected.status)
    }
  }, [selected])

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'aberto':
        return <Clock className="w-5 h-5 text-orange-500" />
      case 'em_andamento':
        return <AlertCircle className="w-5 h-5 text-blue-500" />
      case 'resolvido':
        return <CheckCircle className="w-5 h-5 text-green-500" />
      default:
        return <Clock className="w-5 h-5 text-gray-500" />
    }
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'aberto':
        return 'bg-orange-100 text-orange-800'
      case 'em_andamento':
        return 'bg-blue-100 text-blue-800'
      case 'resolvido':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const getPriorityColor = (prioridade: string) => {
    switch (prioridade) {
      case 'alta':
        return 'bg-red-100 text-red-800'
      case 'normal':
        return 'bg-yellow-100 text-yellow-800'
      case 'baixa':
        return 'bg-green-100 text-green-800'
      default:
        return 'bg-gray-100 text-gray-800'
    }
  }

  const stats = useMemo(() => {
    const total = chamados.length
    const abertos = chamados.filter(c => c.status === 'aberto').length
    const andamento = chamados.filter(c => c.status === 'em_andamento').length
    const resolvidos = chamados.filter(c => c.status === 'resolvido').length
    return { total, abertos, andamento, resolvidos }
  }, [chamados])

  const salvarResposta = async () => {
    if (!selected) return
    setSaving(true)
    try {
      if (respostaTexto.trim()) {
        await responderChamado(selected.id, respostaTexto.trim())
      }
    } finally {
      setSaving(false)
    }
  }

  const salvarStatus = async () => {
    if (!selected) return
    setSaving(true)
    try {
      await atualizarStatusChamado(selected.id, statusEdicao)
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Gerenciamento de Tickets</h1>
          <p className="text-gray-600 mt-2">
            Acompanhe e gerencie os chamados de suporte
          </p>
        </div>
        <button className="admin-button admin-button-primary flex items-center space-x-2">
          <MessageSquare className="w-5 h-5" />
          <span>Novo Ticket</span>
        </button>
      </div>

      {/* Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="admin-card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-orange-500 rounded-lg flex items-center justify-center">
              <Clock className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Abertos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.abertos}</p>
            </div>
          </div>
        </div>
        <div className="admin-card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-blue-500 rounded-lg flex items-center justify-center">
              <AlertCircle className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Em Andamento</p>
              <p className="text-2xl font-bold text-gray-900">{stats.andamento}</p>
            </div>
          </div>
        </div>
        <div className="admin-card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-green-500 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Resolvidos</p>
              <p className="text-2xl font-bold text-gray-900">{stats.resolvidos}</p>
            </div>
          </div>
        </div>
        <div className="admin-card">
          <div className="flex items-center">
            <div className="w-12 h-12 bg-purple-500 rounded-lg flex items-center justify-center">
              <MessageSquare className="w-6 h-6 text-white" />
            </div>
            <div className="ml-4">
              <p className="text-sm font-medium text-gray-600">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Lista de Tickets */}
      <div className="admin-card">
        <h3 className="text-lg font-medium text-gray-900 mb-4">Tickets Recentes</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ticket
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Prioridade
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Usuário
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Tempo
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Ações
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {chamados.map((ticket, index) => (
                <motion.tr
                  key={ticket.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.1 }}
                  className="hover:bg-gray-50"
                >
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">{ticket.titulo}</div>
                      <div className="text-sm text-gray-500">{ticket.descricao}</div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      {getStatusIcon(ticket.status)}
                      <span className={`ml-2 inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(ticket.status)}`}>
                        {ticket.status}
                      </span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(ticket.prioridade)}`}>
                      {ticket.prioridade}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {ticket.usuario_info?.nome || ticket.usuario_info?.email || ticket.usuario_id}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(ticket.created_at).toLocaleDateString('pt-BR')}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    {new Date(ticket.created_at).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                    <button className="text-admin-600 hover:text-admin-900 mr-3" onClick={() => setSelectedId(String(ticket.id))}>
                      Ver
                    </button>
                  </td>
                </motion.tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-xl w-full max-w-3xl p-6 shadow-xl">
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-xl font-semibold text-gray-900">Chamado #{selected.id}</h3>
                <p className="text-gray-600">{selected.titulo}</p>
              </div>
              <button className="text-gray-500 hover:text-gray-700" onClick={() => setSelectedId(null)}>Fechar</button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500">Status</p>
                <div className="mt-1">
                  <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusColor(selected.status)}`}>
                    {selected.status}
                  </span>
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500">Prioridade</p>
                <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getPriorityColor(selected.prioridade)}`}>
                  {selected.prioridade}
                </span>
              </div>
              <div className="bg-gray-50 rounded-lg p-4">
                <p className="text-xs text-gray-500">Criado em</p>
                <p className="text-sm text-gray-800">{new Date(selected.created_at).toLocaleString('pt-BR')}</p>
              </div>
            </div>

            <div className="mb-6">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Descrição</h4>
              <p className="text-gray-800 whitespace-pre-wrap">{selected.descricao}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Responder</h4>
                <textarea
                  className="w-full border border-gray-300 rounded-lg p-3 h-32 focus:ring-2 focus:ring-admin-500 focus:border-transparent"
                  placeholder="Escreva sua resposta para o cliente..."
                  value={respostaTexto}
                  onChange={(e) => setRespostaTexto(e.target.value)}
                />
                <div className="mt-2 flex gap-2">
                  <button
                    className="admin-button admin-button-primary"
                    disabled={saving || loading}
                    onClick={salvarResposta}
                  >
                    {saving ? 'Salvando...' : 'Enviar Resposta'}
                  </button>
                </div>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-700 mb-2">Atualizar Status</h4>
                <select
                  className="w-full border border-gray-300 rounded-lg p-3 focus:ring-2 focus:ring-admin-500 focus:border-transparent"
                  value={statusEdicao}
                  onChange={(e) => setStatusEdicao(e.target.value as any)}
                >
                  <option value="aberto">Aberto</option>
                  <option value="em_andamento">Em andamento</option>
                  <option value="resolvido">Resolvido</option>
                  <option value="fechado">Fechado</option>
                </select>
                <div className="mt-2">
                  <button
                    className="admin-button admin-button-secondary"
                    disabled={saving || loading}
                    onClick={salvarStatus}
                  >
                    {saving ? 'Salvando...' : 'Salvar Status'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default Tickets
